import React, { PropsWithChildren } from 'react';
import {
  Action,
  ThunkAction,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit';
import appSlice, {
  decreaseFontSize,
  increaseFontSize,
  toggleLoading,
  notificationCount,
  cartCount,
} from './slices/appSlice';
import authSlice, {
  logOn,
  logOff,
  logonThunk,
  toggleSfvAttempt,
  setHasProfile,
  setHasCspProfile,
} from './slices/authSlice';
import profileSwitchSlice from './slices/profileSwitchSlice';
import {
  Provider,
  TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';

const rootReducer = combineReducers({
  app: appSlice,
  auth: authSlice,
  profileSwitch: profileSwitchSlice,
});

export const setupStore = (preloadedState?: Partial<RootState>) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
  });

export const store = configureStore({
  reducer: rootReducer,
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof rootReducer>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
interface ICartCount {
  productCount: number;
  subscriptionCount: number;
}

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export function useStore() {
  const auth = useAppSelector((state) => state.auth);
  const app = useAppSelector((state) => state.app);
  const dispatch = useDispatch<AppDispatch>();

  return {
    app,
    auth,
    logon: () => dispatch(logonThunk()),
    authenticate: () => dispatch(logOn()),
    logoff: () => dispatch(logOff()),
    toggleLoading: (loading: boolean) => dispatch(toggleLoading(loading)),
    increaseFontSize: () => dispatch(increaseFontSize()),
    decreaseFontSize: () => dispatch(decreaseFontSize()),
    notificationCount: (count: number) => dispatch(notificationCount(count)),
    cartCount: (count: ICartCount) => dispatch(cartCount(count)),
    toggleSfvAttempt: (bool = true) => dispatch(toggleSfvAttempt(bool)),
    setHasProfile: (bool: boolean) => dispatch(setHasProfile(bool)),
    setHasCspProfile: (bool: boolean) => dispatch(setHasCspProfile(bool)),
  };
}

export function StoreProvider(props: PropsWithChildren) {
  return <Provider store={store}>{props.children}</Provider>;
}
